import React from 'react';
import { Box, Button, Link as MuiLink, Paper, Typography, useTheme } from '@mui/material';
import { Instagram as InstagramIcon, HeadphonesOutlined, CheckCircleOutline, OpenInNew } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { isVisible } from '../../corelogic/model/section';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { getCardStyles, getSecondaryButtonStyles, textMeasure } from '../../shared/styles';
import { SEO } from './SEO';
import { BlockRenderer } from './blocks/BlockRenderer';
import { PageSections } from './PageSections';
import { Section, PageHeader, SectionHeading } from './PageSection';

const contentUseCase = new ContentUseCase();

/**
 * The podcast page renders from content: as soon as `podcast.episodes` and
 * `podcast.platforms` are filled in, the episode list and platform links
 * appear and the "coming soon" panel disappears. No code change needed.
 */
export const PodcastPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getPodcastContent(language);
  const social = contentUseCase.getSocialMediaContent(language);
  const addedBefore = (slot: string) =>
    contentUseCase.getExtraBlocksBefore('podcast', slot, language);

  // The lists appear once they are filled in, and can be switched off in the
  // editing interface even when they are. Without episodes the "coming soon"
  // panel takes their place.
  const showPlatforms = isVisible(content.platforms) && content.platforms.items.length > 0;
  const showEpisodes = isVisible(content.episodes) && content.episodes.items.length > 0;
  const showComingSoon = !showEpisodes && isVisible(content.comingSoon);

  const dateFormatter = new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const sections: Record<string, React.ReactNode> = {
    platforms: showPlatforms && (
      <Section labelledBy="platforms">
        <SectionHeading id="platforms" title={content.platforms.title} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {content.platforms.items.map((platform) => (
            <Button
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={getSecondaryButtonStyles(theme)}
              startIcon={<HeadphonesOutlined />}
            >
              {platform.name}
            </Button>
          ))}
        </Box>
      </Section>
    ),

    // The episodes and the panel that stands in for them while there are none
    // are one section: they are the same place on the page.
    episodes: (
      <>
        {showEpisodes && (
          <Section labelledBy="episodes">
            <SectionHeading id="episodes" title={content.episodes.title} />
            {/* One card per row, so the list takes the measure rather than the
                section: stretched to the full width a card held a two-word
                title against a metre of empty paper. */}
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 3, ...textMeasure }}>
              {content.episodes.items.map((episode) => (
                <Paper key={episode.url} component="li" elevation={0} sx={getCardStyles(theme, 'outlined')}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1, color: 'text.secondary' }}>
                    <Typography variant="body2" component="time" dateTime={episode.date}>
                      {dateFormatter.format(new Date(episode.date))}
                    </Typography>
                    <Typography variant="body2">·</Typography>
                    <Typography variant="body2">{episode.duration}</Typography>
                  </Box>
                  <Typography variant="h4" component="h3" sx={{ mb: 1.5 }}>
                    {episode.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {episode.description}
                  </Typography>
                  <MuiLink
                    href={episode.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.dark',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {content.title}
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </MuiLink>
                </Paper>
              ))}
            </Box>
          </Section>
        )}

        {showComingSoon && (
          <Section labelledBy="coming-soon">
            <Paper elevation={0} sx={{ ...getCardStyles(theme), textAlign: 'center', p: { xs: 4, md: 6 }, '&:hover': {} }}>
              <HeadphonesOutlined sx={{ fontSize: 44, color: 'primary.dark', mb: 2 }} />
              <Typography id="coming-soon" variant="h3" component="h2" sx={{ mb: 2 }}>
                {content.comingSoon.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, ...textMeasure, mx: 'auto' }}>
                {content.comingSoon.description}
              </Typography>
              <Button
                href={social.instagram.url}
                target="_blank"
                rel="noopener noreferrer me"
                sx={getSecondaryButtonStyles(theme)}
                startIcon={<InstagramIcon />}
              >
                {content.comingSoon.cta}
              </Button>
            </Paper>
          </Section>
        )}
      </>
    ),

    accessibility: isVisible(content.accessibility) && (
      <Section labelledBy="accessibility">
        <SectionHeading id="accessibility" title={content.accessibility.title} />
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2, ...textMeasure }}>
          {content.accessibility.points.map((point) => (
            <Box component="li" key={point} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <CheckCircleOutline sx={{ color: 'primary.dark', fontSize: 22, mt: 0.4, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {point}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>
    ),
  };

  return (
    <Box>
      <SEO page="podcast" />

      <BlockRenderer blocks={addedBefore('header')} />

      <PageHeader title={content.title} subtitle={content.subtitle}>
        <Typography variant="body1" sx={{ color: 'text.secondary', ...textMeasure }}>
          {content.description}
        </Typography>
      </PageHeader>

      <PageSections
        page="podcast"
        sections={sections}
        order={content.sectionOrder}
        addedBefore={addedBefore}
      />
    </Box>
  );
};
